import { trackAnalyticsEvent } from '@autone/analytics';
import { useAutoneTranslation } from '@autone/translations';
import {
  cn,
  Dialog,
  DialogClose,
  DialogContent,
  DialogFooter,
  DialogTitle,
  DialogTrigger,
  DropArea,
  TailwindButton,
  TailwindForm,
  TailwindFormField,
} from '@autone/ui';
import { Button, type ButtonProps } from '@autone/ui-next/atoms';
import { UploadIcon } from '@autone/ui-next/icons';
import { EXCEL_FILE_TYPES, uploadFileToUrl } from '@autone/utils';
import { zodResolver } from '@hookform/resolvers/zod';
import { Upload } from 'lucide-react';
import { enqueueSnackbar } from 'notistack';
import { useCallback, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';

import { type APP_NAME } from '../../../consts';
import { useAssortmentTemplateImport } from '../../../hooks/useAssortmentTemplateImport';
import { AggregationTypeRadioToggleGroup } from '../aggregation-type-radio-toggle-group';
import { ModalFooterLoadingIndicator } from '../modal-footer-loading-indicator';

import { importErrorCodeToMessageTranslationKeyMap } from './UploadTemplateModal.helpers';
import {
  uploadAssortmentTemplateFormSchema,
  type UploadAssortmentTemplateFormSchema,
} from './UploadTemplateModal.schema';

interface UploadTemplateModalProps {
  variant?: ButtonProps['variant'];
}

export const UploadTemplateModal = ({
  variant,
}: UploadTemplateModalProps = {}) => {
  const [dialogOpen, setDialogOpen] = useState(false);

  const { common, t } = useAutoneTranslation<typeof APP_NAME>();
  const [fileUploadResult, setFileUploadResult] = useState<number | null>(null);

  const form = useForm<UploadAssortmentTemplateFormSchema>({
    mode: 'onChange',
    resolver: zodResolver(uploadAssortmentTemplateFormSchema),
    defaultValues: {
      aggregationType: 'product_location',
    },
  });

  const {
    startTemplateImport,
    importIsLoading,
    importIsSuccess,
    importIsError,
  } = useAssortmentTemplateImport({
    onImportUrlReady: async (url) => {
      const result = await uploadFileToUrl({
        file: form.getValues('file'),
        url,
      });
      if (result) {
        setFileUploadResult(result.status);
      }
    },
    onImportFailed: (error) => {
      enqueueSnackbar({
        message: t(
          importErrorCodeToMessageTranslationKeyMap[error] ??
            'upload-template-modal.upload-error.unknown-error',
        ),
        variant: 'error',
        autoHideDuration: 10_000,
      });
    },
  });

  const handleOpenChange = useCallback(
    (open: boolean) => {
      if (!open) {
        form.reset();
      }
      setDialogOpen(open);
    },
    [form],
  );

  const handleStartTemplateImport = useCallback(
    ({ aggregationType }: UploadAssortmentTemplateFormSchema) => {
      trackAnalyticsEvent({
        category: 'assortment_template_import_export',
        action: 'upload_template_clicked',
        properties: {
          aggregation_type: aggregationType,
        },
      });
      startTemplateImport({
        body: {
          aggregation_type: aggregationType,
        },
      });
    },
    [startTemplateImport],
  );

  const loadingText = useMemo(() => {
    if (fileUploadResult === null) {
      return t('upload-template-modal.uploading-your-file');
    }
    if (fileUploadResult === 200) {
      return t('upload-template-modal.processing-your-upload-loader');
    }
  }, [fileUploadResult, t]);

  const shouldRenderLoader = fileUploadResult !== null || importIsLoading;
  const disabled = !form.formState.isValid || importIsLoading;

  return (
    <Dialog open={dialogOpen} onOpenChange={handleOpenChange}>
      <DialogTrigger asChild>
        <Button
          id="upload-template"
          loading={importIsLoading}
          aria-label={common('upload-template')}
          variant={variant}
        >
          <UploadIcon />
          {common('upload-template')}
        </Button>
      </DialogTrigger>
      <DialogContent
        className="flex flex-col gap-0 p-0 max-w-[1000px] w-[800px] max-h-[90vh]"
        data-testid={'upload-template-modal-content'}
      >
        <DialogTitle className="p-6 border-b border-b-grey-10">
          {t('upload-template-modal.modal-header')}
        </DialogTitle>
        <TailwindForm {...form}>
          <form className="flex flex-col overflow-y-scroll">
            <div className="flex flex-col p-6 gap-8 grow">
              {/** AGGREGATION TYPE SELECTION */}
              <div className="flex flex-col gap-2 grow">
                <p className="text-base font-medium">
                  {t('upload-template-modal.granularity-section-header')}
                </p>
                <AggregationTypeRadioToggleGroup
                  control={form.control}
                  name={'aggregationType'}
                />
              </div>
            </div>

            {/** FILE UPLOAD AREA */}
            <div className="flex flex-col grow p-6 pt-0">
              <TailwindFormField
                control={form.control}
                name={'file'}
                render={({ field: { onChange, value } }) => (
                  <DropArea
                    onDrop={(files) => {
                      if (files.length > 0) {
                        onChange(files[0]);
                      }
                    }}
                    accept={EXCEL_FILE_TYPES}
                    maxFiles={1}
                    renderChildren={({ isDragging }) => {
                      const acceptedFile = value;
                      const fileName: string | undefined = acceptedFile?.name;

                      return (
                        <div
                          className={cn(
                            'flex flex-col items-center justify-center gap-1 grow h-[300px] bg-grey-5 rounded-lg hover:bg-grey-10 hover:cursor-pointer transition-colors',
                            isDragging && 'bg-grey-10',
                          )}
                        >
                          <Upload />
                          <p className="text-base">
                            {fileName ??
                              t('upload-template-modal.upload-file-prompt')}
                          </p>
                        </div>
                      );
                    }}
                  />
                )}
              />
            </div>
          </form>
        </TailwindForm>
        <DialogFooter className="p-6 border-t border-t-grey-10">
          {shouldRenderLoader && (
            <ModalFooterLoadingIndicator
              complete={importIsSuccess}
              error={importIsError}
              loadingText={loadingText}
              completeText={t('upload-template-modal.upload-complete')}
              onCompleteAnimationEnd={() => {
                setFileUploadResult(null);
              }}
            />
          )}
          <DialogClose asChild>
            <TailwindButton variant={'outlined'} color={'secondary'}>
              {common('buttons.cancel')}
            </TailwindButton>
          </DialogClose>
          <TailwindButton
            variant={'contained'}
            color={'primary'}
            disabled={disabled}
            loading={importIsLoading}
            onClick={form.handleSubmit(handleStartTemplateImport)}
          >
            {common('upload-template')}
          </TailwindButton>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
